import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';

import OperationProgress from '../../../pages/portal/securityhub/OperationProgress';
import { POLL_INTERVAL_MS } from '../../../pages/portal/securityhub/pollInterval';
import { PortalErrorState } from '../PortalStates';
import {
  CK, FONT_BODY, actionRowSx, bodyCellSx, emptyTextSx, headCellSx, listCardSx, rowSx,
  sectionHeadingSx, statusChipSx, tableHeadRowSx,
} from '../../../pages/portal/portalStyles';

// Prism keeps a scoped inline policy on each enrolled account's access role.
// These are the three states the backend reports per account.
const STATE_META = {
  current: { intent: 'success', label: 'Current' },
  outdated: { intent: 'warning', label: 'Needs update' },
  missing: { intent: 'error', label: 'Missing' },
};

const needsFix = (state) => state === 'outdated' || state === 'missing';

/**
 * RolePermissionsPanel — per-capability grant status with a "Fix roles" action.
 *
 * The scan is asynchronous: startScan() returns a job, which we poll until it
 * leaves "running". Fix is fire-and-forget, so afterwards we simply re-scan to
 * show the outcome.
 *
 * All three API functions are injected so one component serves every capability.
 */
export default function RolePermissionsPanel({ startScan, getJob, fix, capabilityLabel }) {
  const [job, setJob] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [error, setError] = useState('');

  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Unmount guard: a leaked interval would keep hitting the API after the user
  // navigates away, and setState on an unmounted component would warn.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const runScan = useCallback(async () => {
    stopPolling();
    setError('');
    setScanning(true);
    try {
      const res = await startScan();
      const started = res?.data;
      if (!started?.id) {
        throw new Error('Could not start the permissions scan');
      }
      if (!mountedRef.current) return;
      setJob(started);

      // Already terminal (e.g. re-joined a finished job) — nothing to poll.
      if (started.status !== 'running') {
        setScanning(false);
        return;
      }

      timerRef.current = setInterval(async () => {
        try {
          const next = (await getJob(started.id))?.data;
          if (!mountedRef.current || !next) return;
          setJob(next);
          if (next.status !== 'running') {
            stopPolling();
            setScanning(false);
          }
        } catch (e) {
          if (!mountedRef.current) return;
          stopPolling();
          setScanning(false);
          setError(e?.message || 'Failed to read the permissions scan');
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      if (!mountedRef.current) return;
      setScanning(false);
      setError(e?.message || 'Failed to start the permissions scan');
    }
  }, [startScan, getJob, stopPolling]);

  useEffect(() => {
    runScan();
    // runScan is stable via useCallback; re-running on identity change is correct.
  }, [runScan]);

  const handleFix = async () => {
    setFixing(true);
    setError('');
    try {
      await fix();
      if (!mountedRef.current) return;
      await runScan();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e?.message || 'Failed to re-apply permissions');
    } finally {
      if (mountedRef.current) setFixing(false);
    }
  };

  const results = job?.results || [];
  const fixable = results.filter((r) => needsFix(r.state)).length;
  const busy = scanning || fixing;
  const complete = job?.status === 'completed';

  // Keep the last completed scan on screen while a re-check runs (dimmed) so the
  // panel doesn't collapse to a progress bar and snap back to full height.
  const lastResultsRef = useRef([]);
  if (complete) lastResultsRef.current = results;
  const shownResults = complete ? results : lastResultsRef.current;

  return (
    <Box>
      <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Account permissions</Typography>
      <Typography
        sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted, mb: 2 }}
      >
        {capabilityLabel} works by assuming a role in each enrolled account. Prism keeps a
        scoped IAM policy on that role listing only the actions this service needs.
        &ldquo;Fix roles&rdquo; re-applies it — use it if a policy was removed in AWS, or after
        an update that needs new permissions.
      </Typography>

      {error && <PortalErrorState error={error} onRetry={runScan} sx={{ mb: 2 }} />}

      {job?.status === 'failed' && (
        <PortalErrorState
          error={job.errorMessage || 'The permissions scan failed.'}
          onRetry={runScan}
          sx={{ mb: 2 }}
        />
      )}

      {scanning && (
        <OperationProgress
          percent={job?.progress > 0 ? job.progress : null}
          label="Checking account permissions…"
        />
      )}

      {shownResults.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            ...listCardSx,
            mb: 2,
            overflowX: 'auto',
            // Dim whenever the table is showing the PREVIOUS scan — while a
            // re-check runs and also after a failed scan — so stale rows are
            // never presented as the current result.
            opacity: complete ? 1 : 0.5,
            transition: 'opacity 0.2s ease',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Permissions</TableCell>
                <TableCell sx={headCellSx}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shownResults.map((r, i) => {
                const meta = STATE_META[r.state] || { intent: 'neutral', label: r.state };
                return (
                  <TableRow key={r.accountId} sx={rowSx(i)}>
                    <TableCell sx={bodyCellSx}>{r.accountId}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={meta.label} sx={statusChipSx(meta.intent)} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: CK.muted }}>
                      {r.error || ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {complete && results.length === 0 && (
        <Typography sx={{ ...emptyTextSx, mb: 2 }}>
          No enrolled accounts to check yet — enroll an account to start tracking its role permissions.
        </Typography>
      )}

      <Box sx={actionRowSx}>
        <Button
          variant="contained"
          onClick={handleFix}
          disabled={busy || fixable === 0}
        >
          {fixing ? 'Applying…' : 'Fix roles'}
        </Button>
        <Button variant="outlined" onClick={runScan} disabled={busy}>
          Re-check
        </Button>
        {complete && fixable === 0 && results.length > 0 && (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.muted }}>
            All accounts are up to date.
          </Typography>
        )}
        {complete && fixable > 0 && (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.muted }}>
            {fixable} {fixable === 1 ? 'account needs' : 'accounts need'} attention.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
